import {isRejectedWithValue, Middleware} from '@reduxjs/toolkit';

import {INVALID_VERSION_CODE} from 'app/utils/constants';
import {sendSentryError, withSentryScope} from 'app/utils/sentry';

import type {AppDispatchType} from '../AppStore';
import type {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';

type CustomCodeFetchQueryError = FetchBaseQueryError & {
  data?: {code?: string};
};

// this middleware captures request errors, inspect them, and send all details to our Sentry
export const ErrorLoggerMiddleware: Middleware =
  () => (next: AppDispatchType) => (action) => {
    if (
      isRejectedWithValue(action) &&
      action.payload?.status &&
      action.meta?.baseQueryMeta?.request
    ) {
      const error: CustomCodeFetchQueryError = action.payload;

      // we are not interested in 404 up to 400 and some network errors (except 402)
      const ignoredStatuses = [400, 401, 403, 404, 'FETCH_ERROR'];

      if (
        !ignoredStatuses.includes(error.status) &&
        error.data?.code !== INVALID_VERSION_CODE // when version is out of date.
      ) {
        // postpone the error sending to the next tick
        setTimeout(async () => {
          const {request, response}: FetchBaseQueryMeta =
            action.meta.baseQueryMeta;

          let body = '';

          try {
            body = await request.text();
          } catch (e) {
            // continue regardless of error
          }

          withSentryScope((scope) => {
            scope.setLevel('error');

            scope.setExtra('request', {
              method: request.method,
              url: request.url,
              body: limitSize(body),
            });

            scope.setExtra(
              'response',
              response
                ? {
                    status: response.status,
                    response:
                      typeof error.data === 'string'
                        ? // 5kb of data is enough
                          limitSize(error.data)
                        : undefined,
                  }
                : undefined
            );
            sendSentryError(
              `Fetch Error ${error.status} on ${request.method} to ${request.url}`
            );
          });
        }, 0);
      }
    }

    return next(action);
  };

function limitSize(content: string, maxSize = 1024 * 5): string {
  return content.length <= maxSize
    ? content
    : content.slice(0, maxSize) + ' [trimmed]';
}
